import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
} from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import ServiceCard from "../../shared/Optimized/ServiceCard";
import ServiceModal from "../../shared/Optimized/ServiceModal";
import AnimatedButton from "../../shared/MicroInteractions/AnimatedButton";
import { useTranslation } from "react-i18next";
import { getAllServices } from "./constants/services";


// Animations with Framer Motion
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
};

const zoomIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.8 } },
};

const Services = () => {
  const { t, i18n } = useTranslation();
  const [openModal, setOpenModal] = useState(null);

  // Parallax effect para el header
  const { scrollY } = useScroll();
  const yHeader = useTransform(scrollY, [0, 300], [0, 100]);
  const opacityHeader = useTransform(scrollY, [0, 200], [1, 0.3]);

  const translatedServices = React.useMemo(() => getAllServices(t), [t, i18n.language]);

  // Memoizar callbacks para optimización
  const handleModalOpen = React.useCallback((modalKey) => {
    setOpenModal(modalKey);
  }, []);

  const handleModalClose = React.useCallback(() => {
    setOpenModal(null);
  }, []);

  // Buscar el servicio actual para el modal
  const currentService = React.useMemo(
    () => translatedServices.find((s) => s.key === openModal),
    [openModal, translatedServices]
  );

  return (
    <Box>
      <Helmet>
        <title>{t('services.title')}</title>
        <meta name="description" content={t('services.description')} />
        <meta property="og:title" content={t('services.ogTitle')} />
        <meta property="og:description" content={t('services.ogDescription')} />
        <meta property="og:image" content="https://itenginesparks.com/og-banner.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="IT Engine Sparks — Web & Software Development" />
        <meta property="og:url" content="https://itenginesparks.com/services" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://itenginesparks.com/services" />
        <meta name="twitter:title" content={t('services.ogTitle')} />
        <meta name="twitter:description" content={t('services.description')} />
        <meta name="twitter:image" content="https://itenginesparks.com/og-banner.png" />
      </Helmet>
      {/* Header Section con Parallax */}
      <Box
        component={motion.div}
        style={{ y: yHeader }}
        sx={{
          position: "relative",
          color: "white",
          py: 10,
          textAlign: "center",
          overflow: "hidden",
          backgroundImage: "url('/images/consulting.webp')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: "linear-gradient(135deg, rgba(8,18,38,0.88) 0%, rgba(21,101,192,0.75) 60%, rgba(180,70,0,0.50) 100%)",
            zIndex: 0,
          },
        }}
      >
        <Container
          component={motion.div}
          style={{ opacity: opacityHeader }}
          sx={{ position: "relative", zIndex: 1 }}
        >
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontSize: { xs: "2.2rem", md: "3.2rem" },
                fontWeight: 800,
                color: "#ffffff",
                letterSpacing: "-0.02em",
                textShadow: "0 2px 20px rgba(0,0,0,0.25)",
              }}
            >
              {t('services.heading')}
            </Typography>
            <Typography
              variant="h5"
              sx={{ color: "rgba(255,255,255,0.88)", fontWeight: 400 }}
            >
              {t('services.subheading')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Services Grid Section */}
      <Container sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {translatedServices.map((service) => (
            <Grid item xs={12} sm={6} md={4} key={service.key}>
              <ServiceCard
                service={service}
                onClick={handleModalOpen}
                variants={zoomIn}
              />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Modal optimizado - solo renderiza el modal activo */}
      <ServiceModal
        service={currentService}
        isOpen={!!openModal}
        onClose={handleModalClose}
      />

      {/* Call to Action Section */}
      <Box
        sx={{
          position: "relative",
          background: (theme) => theme.gradients.secondary,
          color: "#ffffff",
          py: { xs: 8, md: 10 },
          textAlign: "center",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "radial-gradient(ellipse at 30% 50%, rgba(255,255,255,0.08) 0%, transparent 60%)",
            zIndex: 0,
          },
        }}
      >
        <Container sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.8rem", md: "2.4rem" },
              color: "#ffffff",
              letterSpacing: "-0.01em",
              mb: 2,
            }}
          >
            {t('services.getStarted')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "1rem", md: "1.1rem" },
              mb: 4,
              color: "rgba(255,255,255,0.85)",
              maxWidth: "560px",
              mx: "auto",
            }}
          >
            {t('services.getStartedDescription')}
          </Typography>
          <Link to="/contact" style={{ textDecoration: "none" }}>
            <AnimatedButton
              variant="outlined"
              size="large"
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.75)",
                borderWidth: 2,
                px: 5,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 700,
                borderRadius: "8px",
                "&:hover": {
                  borderColor: "#fff",
                  borderWidth: 2,
                  backgroundColor: "rgba(255,255,255,0.14)",
                },
              }}
            >
              {t('services.contactButton')}
            </AnimatedButton>
          </Link>
        </Container>
      </Box>
    </Box>
  );
};

export default Services;
